import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { useLanguage } from '../../../contexts/LanguageContext';

const DocumentPreviewModal = ({ isOpen, onClose, document, currentLanguage }) => {
  const { t } = useLanguage();
  if (!isOpen || !document) return null;

  const formatDate = (date) => {
    return new Intl.DateTimeFormat('en-US', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    })?.format(date);
  };

  const statusConfig = {
    signed: {
      label: t('documents.signed'),
      color: 'text-success',
      bgColor: 'bg-success/10',
      icon: 'CheckCircle'
    },
    pending: {
      label: t('documents.pending'),
      color: 'text-warning',
      bgColor: 'bg-warning/10',
      icon: 'Clock'
    },
    completed: {
      label: t('documents.completed'),
      color: 'text-success',
      bgColor: 'bg-success/10',
      icon: 'CheckCircle2'
    },
    sent: {
      label: t('documents.sent'),
      color: 'text-primary',
      bgColor: 'bg-primary/10',
      icon: 'Send'
    }
  };

  const status = statusConfig?.[document?.status];

  const handleDownload = () => {
    console.log('Downloading document:', document?.name);
    alert(currentLanguage === 'fr' 
      ? `Téléchargement de: ${document?.name}` 
      : `تحميل: ${document?.name}`);
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" 
      onClick={onClose}
    >
      <div 
        className="bg-card rounded-lg border border-border shadow-warm max-w-4xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e?.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="FileText" size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">
                {t('documents.documentPreview')}
              </h2>
              <p className="caption text-muted-foreground">{document?.name}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            iconName="X"
            onClick={onClose}
            aria-label={t('documents.close')}
          />
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Document Info */}
          {document?.pilgrim && (
            <div className="flex items-start gap-4 pb-6 border-b border-border">
              <div className="w-16 h-16 rounded-full overflow-hidden">
                <Image
                  src={document?.pilgrim?.avatar}
                  alt={document?.pilgrim?.avatarAlt}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-foreground">{document?.pilgrim?.name}</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  {t('documents.pilgrim')}
                </p>
                <div className="flex items-center gap-3 mt-3">
                  <div className={`flex items-center gap-2 px-3 py-1 rounded-full ${status?.bgColor}`}>
                    <Icon name={status?.icon} size={14} className={status?.color} />
                    <span className={`caption font-medium ${status?.color}`}>
                      {status?.label}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Document Details */}
          <div>
            <h4 className="text-sm font-semibold text-foreground mb-3">
              {t('documents.documentDetails')}
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-muted/30 rounded-lg p-4">
                <p className="caption text-muted-foreground mb-1">
                  {t('documents.fileSize')}
                </p>
                <p className="text-sm font-medium text-foreground flex items-center gap-2">
                  <Icon name="HardDrive" size={14} />
                  {document?.size}
                </p>
              </div>
              <div className="bg-muted/30 rounded-lg p-4">
                <p className="caption text-muted-foreground mb-1">
                  {t('documents.uploadDate')}
                </p>
                <p className="text-sm font-medium text-foreground flex items-center gap-2">
                  <Icon name="Calendar" size={14} />
                  {formatDate(document?.uploadDate)}
                </p>
              </div>
              <div className="bg-muted/30 rounded-lg p-4">
                <p className="caption text-muted-foreground mb-1">
                  {t('documents.version')}
                </p>
                <p className="text-sm font-medium text-foreground flex items-center gap-2">
                  <Icon name="GitBranch" size={14} />
                  {t('documents.version')} {document?.version}
                </p>
              </div>
              <div className="bg-muted/30 rounded-lg p-4">
                <p className="caption text-muted-foreground mb-1">
                  {t('documents.status')}
                </p>
                <p className="text-sm font-medium text-foreground">
                  {status?.label}
                </p>
              </div>
            </div>
          </div>

          {/* Preview Area */}
          <div className="bg-muted/30 rounded-lg p-8 min-h-[400px] flex items-center justify-center">
            <div className="text-center">
              <Icon name="FileText" size={64} className="text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground mb-2">
                {t('documents.pdfPreview')}
              </p>
              <p className="caption text-muted-foreground">
                {t('documents.contentWillDisplay')}
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-card border-t border-border px-6 py-4 flex items-center justify-end gap-3">
          <Button
            variant="outline"
            onClick={onClose}
          >
            {t('documents.close')}
          </Button>
          <Button
            variant="default"
            iconName="Download"
            iconPosition="left"
            onClick={handleDownload}
          >
            {t('documents.download')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DocumentPreviewModal;